import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SIGNAL_REJECTION_LABEL, type SignalRejectionReason } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { dirClass, dirLabel, fmtTime } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill } from '../components/ui';
import { SIGNAL_TONE, type SignalView } from '../components/SignalFeedItem';

/**
 * Signal sources (INT-SYN-02, D22): named TradingView sources with secret webhook URLs (shown once; rotate or revoke),
 * the signals they sent and the calls VTF refused, with reasons. Admins and Master Traders.
 */

interface Source {
  id: string;
  name: string;
  tokenPrefix: string;
  status: 'ACTIVE' | 'REVOKED';
  createdBy: { id: string; name: string };
  createdAt: string;
  rotatedAt: string | null;
  revokedAt: string | null;
  revokedBy: { id: string; name: string } | null;
  lastSignalAt: string | null;
  signals: number;
  rejections: number;
  canManage: boolean;
}
interface Rejection {
  id: string;
  source: { id: string; name: string } | null;
  reason: SignalRejectionReason;
  message: string;
  excerpt: string | null;
  ip: string | null;
  count: number;
  createdAt: string;
  lastAt: string;
}

const SAMPLE = `{"v":1,"strategy":"spring","event":"triggered","symbol":"OANDA:XAUUSD","tf":"15","direction":"LONG","price":2401.3,"entry":2405.5,"stop":2398,"targets":[2412.5,2420],"notes":"Absorption at VWAP","time":1793588400000}`;

function NewUrl({ name, url, onClose }: { name: string; url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Card tone="warn" className="flex flex-col gap-2.5 p-4" >
      <h2 className="m-0 text-[15px] font-semibold">Webhook URL for “{name}”</h2>
      <p className="m-0 text-sm text-sub">
        This is the only time the full URL is shown. Paste it into the TradingView alert&apos;s <strong className="text-ink">Webhook URL</strong> field. Anyone with it can post signals as this source — if it leaks, rotate it.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input readOnly aria-label="Webhook URL" value={url} onFocus={(e) => e.target.select()} className="font-mono text-xs" />
        <Button variant="primary" onClick={() => void copy()} className="shrink-0">
          {copied ? 'Copied ✓' : 'Copy URL'}
        </Button>
      </div>
      <Button className="self-start" onClick={onClose}>
        Done
      </Button>
    </Card>
  );
}

function SourceRow({ s, tz, onUrl }: { s: Source; tz: string; onUrl: (name: string, url: string) => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<'rotate' | 'revoke' | null>(null);
  const act = useMutation({
    mutationFn: (what: 'rotate' | 'revoke') => api<{ url?: string; source: Source }>(`/signal-sources/${s.id}/${what}`, { method: 'POST' }),
    onSuccess: (r) => {
      setConfirm(null);
      if (r.url) onUrl(r.source.name, r.url);
      void qc.invalidateQueries({ queryKey: ['signal-sources'] });
    },
  });
  return (
    <div className={cx('flex flex-col gap-2 border-t border-[#1a1f27] px-4 py-3 first:border-t-0', s.status === 'REVOKED' && 'opacity-60')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex flex-wrap items-center gap-2">
          <strong>{s.name}</strong>
          <Pill tone={s.status === 'ACTIVE' ? 'up' : 'neutral'}>{s.status === 'ACTIVE' ? 'Active' : 'Revoked'}</Pill>
        </span>
        {s.canManage && !confirm && (
          <span className="flex gap-2">
            <Button onClick={() => setConfirm('rotate')}>Rotate URL</Button>
            <Button variant="danger" onClick={() => setConfirm('revoke')}>
              Revoke
            </Button>
          </span>
        )}
      </div>
      <span className="font-mono text-xs break-all text-muted">…/webhooks/tradingview/{s.tokenPrefix}…</span>
      <span className="text-xs text-sub">
        {s.signals} signal{s.signals === 1 ? '' : 's'} · last {s.lastSignalAt ? fmtTime(s.lastSignalAt, tz, true) : 'never'} · {s.rejections} refused · created by {s.createdBy.name} {fmtTime(s.createdAt, tz, true)}
        {s.rotatedAt && ` · rotated ${fmtTime(s.rotatedAt, tz, true)}`}
        {s.revokedAt && ` · revoked ${fmtTime(s.revokedAt, tz, true)}${s.revokedBy ? ` by ${s.revokedBy.name}` : ''}`}
      </span>
      {confirm && (
        <div className="flex flex-col gap-2 rounded-lg bg-raised p-3 text-sm">
          <span>
            {confirm === 'rotate'
              ? 'The current URL stops working immediately. Update the webhook URL in every TradingView alert that uses it.'
              : 'The URL stops working for good. Signals already received stay on the floor. This cannot be undone.'}
          </span>
          <span className="flex gap-2">
            <Button variant={confirm === 'revoke' ? 'danger' : 'primary'} onClick={() => act.mutate(confirm)} disabled={act.isPending}>
              {confirm === 'rotate' ? 'Rotate now' : 'Revoke source'}
            </Button>
            <Button onClick={() => setConfirm(null)}>Cancel</Button>
          </span>
        </div>
      )}
      <ErrorText error={act.error ? errorText(act.error) : null} />
    </div>
  );
}

export function SignalSources() {
  const me = useMe();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const [name, setName] = useState('');
  const [shown, setShown] = useState<{ name: string; url: string } | null>(null);
  const sources = useQuery({ queryKey: ['signal-sources'], queryFn: () => api<Source[]>('/signal-sources'), refetchInterval: 30_000 });
  const signals = useQuery({ queryKey: ['signal-sources', 'signals'], queryFn: () => api<SignalView[]>('/signals?limit=30'), refetchInterval: 30_000 });
  const rejections = useQuery({ queryKey: ['signal-sources', 'rejections'], queryFn: () => api<Rejection[]>('/signals/rejections?limit=30'), refetchInterval: 30_000 });
  const create = useMutation({
    mutationFn: () => api<{ url: string; source: Source }>('/signal-sources', { body: { name } }),
    onSuccess: (r) => {
      setShown({ name: r.source.name, url: r.url });
      setName('');
      void qc.invalidateQueries({ queryKey: ['signal-sources'] });
    },
  });

  return (
    <>
      <PageHeader title="Signal sources" sub="TradingView strategy signals post to the floor through these webhook URLs — signals only, never prices." />
      {shown && <NewUrl name={shown.name} url={shown.url} onClose={() => setShown(null)} />}

      <div className="grid items-start gap-3.5 lg:grid-cols-[3fr_2fr]">
        <Card>
          <CardHeader title={<>Sources <span className="font-normal text-muted">· {sources.data?.filter((s) => s.status === 'ACTIVE').length ?? 0} active</span></>} />
          <ErrorText error={sources.error ? errorText(sources.error) : null} />
          {sources.data?.map((s) => <SourceRow key={s.id} s={s} tz={tz} onUrl={(n, url) => setShown({ name: n, url })} />)}
          {sources.isSuccess && !sources.data.length && <Empty>No sources yet. Create one for each scanner or script.</Empty>}
        </Card>
        <Card className="flex flex-col gap-3 p-4">
          <h2 className="m-0 text-[15px] font-semibold">New source</h2>
          <Field label="Name" hint="One per script or scanner, e.g. “Wyckoff scanner”">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Wyckoff scanner" maxLength={80} />
          </Field>
          <ErrorText error={create.error ? errorText(create.error) : null} />
          <Button variant="primary" className="self-start" onClick={() => create.mutate()} disabled={name.trim().length < 2 || create.isPending}>
            Create source
          </Button>
          <details className="text-sm text-sub">
            <summary className="cursor-pointer font-semibold text-ink">Payload format</summary>
            <p className="my-2">
              JSON in the alert message (TradingView sends it as text/plain): one signal, or <code>{'{ "signals": [ … ] }'}</code> for up to 50. <code>event</code> is getting_ready, ready, triggered,
              invalidated or info. Symbols match an instrument&apos;s TradingView ticker (set it on <Link to="/admin/markets">Markets &amp; symbols</Link>), or its plain symbol when only one instrument has it.
            </p>
            <pre className="m-0 overflow-x-auto rounded-lg bg-raised p-3 text-xs whitespace-pre-wrap break-all text-ink2">{SAMPLE}</pre>
            <p className="mt-2 mb-0">The Pine template and setup steps are in the repository under infra/tradingview.</p>
          </details>
        </Card>
      </div>

      <div className="grid items-start gap-3.5 xl:grid-cols-2">
        <Card>
          <CardHeader title="Recent signals" right={<Link to="/floor?type=signals" className="text-sm">On the floor →</Link>} />
          {signals.data?.map((s) => (
            <div key={s.id} className="flex flex-col gap-1 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
              <span className="flex flex-wrap items-center gap-2">
                <Pill tone={SIGNAL_TONE[s.event]}>{s.eventLabel}</Pill>
                <strong>{s.instrument.symbol}</strong>
                <span className="text-sub">
                  {s.strategy} · {s.timeframe}
                </span>
                {s.direction && <span className={cx('text-sm font-semibold', dirClass(s.direction))}>{dirLabel(s.direction)}</span>}
              </span>
              <span className="text-xs text-muted">
                {fmtTime(s.receivedAt, tz, true)} · {s.source.name} · {s.symbol}
                {s.entry && ` · entry ${s.entry}`}
                {s.stop && ` · stop ${s.stop}`}
              </span>
            </div>
          ))}
          {signals.isSuccess && !signals.data.length && <Empty>No signals yet.</Empty>}
        </Card>
        <Card>
          <CardHeader title="Recently refused" right={<span className="text-xs text-muted">newest first</span>} />
          {rejections.data?.map((r) => (
            <div key={r.id} className="flex flex-col gap-1 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
              <span className="flex flex-wrap items-center gap-2">
                <Pill tone={r.reason === 'unknown_token' || r.reason === 'revoked' ? 'down' : 'warn'}>{SIGNAL_REJECTION_LABEL[r.reason] ?? r.reason}</Pill>
                <span className="text-sm">{r.source?.name ?? 'Unknown URL'}</span>
                {r.count > 1 && <span className="text-xs text-muted">× {r.count}</span>}
              </span>
              <span className="text-sm text-ink2">{r.message}</span>
              <span className="text-xs text-muted">
                {fmtTime(r.lastAt, tz, true)}
                {r.ip && ` · from ${r.ip}`}
              </span>
              {r.excerpt && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-sub">What was sent</summary>
                  <pre className="m-0 mt-1 overflow-x-auto rounded bg-raised p-2 whitespace-pre-wrap break-all text-ink2">{r.excerpt}</pre>
                </details>
              )}
            </div>
          ))}
          {rejections.isSuccess && !rejections.data.length && <Empty>Nothing refused.</Empty>}
        </Card>
      </div>
    </>
  );
}
